import React, { useEffect, useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  TableHead,
  Tooltip,
  Skeleton,
} from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import SummarizeIcon from '@mui/icons-material/Summarize'
import { Link } from 'react-router-dom'
import { useGetNexenPoints } from '../../api/point'
import ErrorMessage from '../shared/component/ErrorMessage'
import Fuse from 'fuse.js'
import { NEXEN_POINT_START_DATE, NEXEN_POINT_END_DATE } from '../../constant/point-system-date'

export const NexenTable = (props) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const { salesId, searchFilter, superiorList } = props

  // return to first page when sales id is change
  useEffect(() => {
    setPage(0)
  }, [salesId])

  useEffect(() => {
    setPage(0)
  }, [searchFilter])

  const defaultQueries = {
    startDate: NEXEN_POINT_START_DATE,
    endDate: NEXEN_POINT_END_DATE,
  }

  const nexenPointDataRes = useGetNexenPoints(
    salesId
      ? { salesId, ...defaultQueries }
      : superiorList.length > 0
      ? { superiorList, ...defaultQueries }
      : defaultQueries
  )

  const pointData = useMemo(() => {
    const list = nexenPointDataRes?.data ?? []

    if (!searchFilter) return list.sort((a, b) => b.totalPoint - a.totalPoint)

    const itemFuse = new Fuse(list, {
      shouldSort: true,
      includeScore: true,
      threshold: 0.3,
      ignoreLocation: true,
      keys: ['companyName'],
    })

    const filtereditemFuse = itemFuse.search(searchFilter)

    return filtereditemFuse
      .filter((fuse) => fuse.score < 0.5)
      .map((each) => each.item)
      .sort((a, b) => b.totalPoint - a.totalPoint)
  }, [searchFilter, nexenPointDataRes?.data])

  const arr = useMemo(() => {
    return rowsPerPage > 0
      ? [...pointData].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : pointData
  }, [page, rowsPerPage, pointData])

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - pointData?.length) : 0

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <TableContainer component={Paper}>
      {arr.length > 0 || nexenPointDataRes.isLoading ? (
        <Table sx={{ minWidth: 500 }} aria-label='custom pagination table'>
          <TableHead>
            <TableRow>
              <TableCell>Company Accound Id</TableCell>
              <TableCell align='right'>Company Name</TableCell>
              <TableCell align='right'>Htg Company</TableCell>
              <TableCell align='right'>Total Point</TableCell>
              <TableCell align='right'>Sales Id</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {arr?.length
              ? arr.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell component='th' scope='row'>
                      {row.companyAccountId}
                    </TableCell>
                    <TableCell style={{ width: 160 }} align='right'>
                      {row.companyName}
                    </TableCell>
                    <TableCell style={{ width: 160 }} align='right'>
                      {row.htgCompany}
                    </TableCell>
                    <TableCell style={{ width: 160 }} align='right'>
                      {row.totalPoint.toFixed(2)}
                    </TableCell>
                    <TableCell style={{ width: 160 }} align='right'>
                      {row.salesId}
                    </TableCell>
                    <TableCell style={{ width: 160 }} align='right'>
                      <Link to={`/nexen-summary?htg=${row.htgCompany}&id=${row.companyAccountId}`}>
                        <Tooltip title={`View Summary for ${row.companyAccountId}`}>
                          <IconButton>
                            <SummarizeIcon />
                          </IconButton>
                        </Tooltip>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              : nexenPointDataRes.isLoading &&
                Array(10)
                  .fill('')
                  .map((x, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 20, 30, { label: 'All', value: -1 }]}
                colSpan={6}
                count={pointData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      ) : (
        <ErrorMessage empty message={'No result to display'} />
      )}
    </TableContainer>
  )
}

function TablePaginationActions(props) {
  const theme = useTheme()
  const { count, page, rowsPerPage, onPageChange } = props

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='first page'
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}
